'use client';
import React, { useContext, createContext, useReducer, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { callService } from '@api/remote-call';

import { Action, CommonState, ACTIONS, GuestInformations } from './types';

const getInitialValues = () => {
  let initialGuests = [];
  if (typeof localStorage !== 'undefined') {
    const guests = localStorage.getItem('guests');

    initialGuests = guests ? JSON.parse(guests) : [];
  }

  return initialGuests;
};

const initialValues = {
  guests: getInitialValues(),
};

type CommonDispatch = (action: Action) => void;

const CommonStateContext = createContext<CommonState | undefined>(undefined);
const CommonDispatchContext = createContext<CommonDispatch | undefined>(
  undefined
);

const CommonReducer = (state: CommonState, action: Action): CommonState => {
  switch (action.type) {
    case ACTIONS.SET_GUESTS: {
      const st = {
        ...state,
        guests: [...action.payload],
      };

      return st;
    }
    case ACTIONS.REMOVE_GUEST:
      return {
        ...state,
        guests: state.guests.filter((guest) => guest.id !== action.payload),
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const CommonProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const [state, dispatch] = useReducer(CommonReducer, initialValues);

  useEffect(() => {
    if (state.guests.length) {
      localStorage.setItem('guests', JSON.stringify(state.guests));
    }
  }, [state.guests]);

  return (
    <CommonStateContext.Provider value={state}>
      <CommonDispatchContext.Provider value={dispatch}>
        {children}
      </CommonDispatchContext.Provider>
    </CommonStateContext.Provider>
  );
};

export const useCommonState = (): CommonState => {
  const context = useContext(CommonStateContext);
  if (context === undefined) {
    throw new Error('useCommonState must be used within a CommonProvider');
  }
  return context;
};

export const useSetGuests = (): ((payload: GuestInformations[]) => void) => {
  const dispatch = useContext(CommonDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetBasketId must be used within a CommonProvider');
  }
  return (payload: GuestInformations[]) =>
    dispatch({ type: ACTIONS.SET_GUESTS, payload: payload });
};

export const useRemoveGuest = (): ((payload: string) => void) => {
  const dispatch = useContext(CommonDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetBasketId must be used within a CommonProvider');
  }
  return (payload: string) =>
    dispatch({ type: ACTIONS.REMOVE_GUEST, payload: payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(CommonDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a CommonProvider');
  }
  return () => dispatch({ type: ACTIONS.CLEAR });
};

export const useAirlines = () => {
  const { data = [] } = useQuery(
    ['airlines'],
    () =>
      callService({
        service: 'FlightsService',
        method: 'listAirLines',
        version: 'v3',
      }),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    }
  );
  return data ?? [];
};

export default CommonProvider;
